import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader, SectionTitle, EditorialButton } from "@/lib/editorial";
import { CHROME_IMAGES } from "@/data/mockData";
import { discoverApi, agentsApi } from "@/lib/api";
import { Compass, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtCET } from "@/lib/utils";

// discover_tick runs at 08:00 and 20:00 UTC (app.config.DISCOVER_CRON_HOURS_UTC).
function nextDiscoverRun() {
  const now = new Date();
  const candidates = [8, 20].map((h) => {
    const d = new Date(now);
    d.setUTCHours(h, 0, 0, 0);
    if (d <= now) d.setUTCDate(d.getUTCDate() + 1);
    return d;
  });
  const next = candidates.sort((a, b) => a - b)[0];
  return fmtCET(next.toISOString());
}

export default function Discovery() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["discover"], queryFn: discoverApi.get });
  const [pendingOffers, setPendingOffers] = useState({});
  // Track which agents are currently running a discovery
  const [runningAgents, setRunningAgents] = useState({});

  const runDiscover = useMutation({
    mutationFn: (id) => discoverApi.run(id),
    onSuccess: (_, id) => {
      toast(`Discovery started for agent #${id}. Polling for results…`);
      setRunningAgents((prev) => ({ ...prev, [id]: true }));
      // Poll status every 5s until finished
      const interval = setInterval(async () => {
        try {
          const status = await discoverApi.status(id);
          if (!status.running) {
            clearInterval(interval);
            setRunningAgents((prev) => ({ ...prev, [id]: false }));
            qc.invalidateQueries({ queryKey: ["discover"] });
            if (status.lastRun) {
              const lr = status.lastRun;
              const errNote = lr.errors > 0 ? ` · ${lr.errors} search error(s)` : "";
              const sheetNote = lr.totalContacts != null ? ` · ${lr.totalContacts} total in DB` : "";
              if (!lr.ok && lr.errors > 0) {
                toast.error(`Discovery failed for agent #${id}. Check logs.`);
              } else {
                toast.success(
                  `✅ Discovery done for agent #${id}: ${lr.prospectsFound} new prospects found${errNote}${sheetNote}.`
                );
              }
            }
          }
        } catch {}
      }, 5000);
      // Stop polling after 10 minutes max
      setTimeout(() => {
        clearInterval(interval);
        setRunningAgents((prev) => ({ ...prev, [id]: false }));
      }, 600_000);
    },
    onError: (e) => toast.error(e.message),
  });

  const saveOffers = useMutation({
    mutationFn: ({ id, offers }) => agentsApi.saveDiscoverOffers(id, offers),
    onSuccess: (_, { id }) => {
      toast("Offer targeting saved.");
      setPendingOffers((p) => { const n = { ...p }; delete n[id]; return n; });
      qc.invalidateQueries({ queryKey: ["discover"] });
    },
  });

  function toggleOffer(agentId, offerId, currentOffers) {
    const prev = pendingOffers[agentId] !== undefined ? pendingOffers[agentId] : new Set(currentOffers);
    const next = new Set(prev);
    next.has(offerId) ? next.delete(offerId) : next.add(offerId);
    setPendingOffers({ ...pendingOffers, [agentId]: next });
  }

  if (isLoading) return <div className="px-8 py-12 text-[var(--muted-light)]">Loading…</div>;

  const offers = data?.offers ?? [];

  return (
    <div className="text-[var(--ink)]">
      <div className="relative bg-[var(--ink)] text-white overflow-hidden">
        <img src={CHROME_IMAGES.headerDecor} alt="" className="chrome-img absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] via-[var(--ink)]/85 to-transparent" />
        <div className="relative px-8 lg:px-14 py-16">
          <PageHeader eyebrow="Section 01" tape="Prospect" rest="discovery"
            sub="Research via LinkedIn search, People You May Know, or manual add — whichever agent offers/campaigns are configured. No Google Sheets."
            dark testId="discovery-title" />
        </div>
      </div>

      {/* Schedule */}
      <section className="px-8 lg:px-14 py-12 grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-[var(--rule-light)]">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-2">Schedule</div>
          <div className="font-display text-[24px] leading-none">Daily at 08:00 &amp; 20:00 UTC</div>
          <p className="text-[13px] text-[var(--muted-light)] mt-3 leading-relaxed">Runs for each active agent's own configured offers/campaigns — not necessarily all 12. LinkedIn search is also capped by LinkedIn's own monthly search-result limit, separate from this schedule.</p>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-2">Next run</div>
          <div className="font-display text-[24px] leading-none">{nextDiscoverRun()}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-2">History entries</div>
          <div className="font-display text-[24px] leading-none">{(data?.history ?? []).length}</div>
        </div>
      </section>

      {/* Offer ICP map */}
      <section className="px-8 lg:px-14 py-12 border-b border-[var(--rule-light)]">
        <SectionTitle eyebrow="Reference">Offer ICP map</SectionTitle>
        <div className="border border-[var(--rule-light)]">
          <table className="w-full text-[13px]">
            <thead className="bg-[var(--paper-2)]">
              <tr className="text-left"><Th align="center">#</Th><Th>Offer</Th><Th>ICP search signals</Th></tr>
            </thead>
            <tbody>
              {offers.map((o) => (
                <tr key={o.id} className="border-t border-[var(--rule-light)]">
                  <td className="py-3 px-4 text-center font-display text-[18px] text-[var(--blue)]">{o.id}</td>
                  <td className="py-3 px-4 font-display text-[15px]">{o.name}</td>
                  <td className="py-3 px-4 text-[var(--muted-light)] text-[12.5px]">{o.icp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Per-agent cards */}
      <section className="px-8 lg:px-14 py-12 grid grid-cols-1 xl:grid-cols-2 gap-6">
        {(data?.agents ?? []).map((a) => {
          const currentOfferIds = a.discoverOffers ?? [];
          const activeSet = pendingOffers[a.id] !== undefined ? pendingOffers[a.id] : new Set(currentOfferIds);
          const isRunning = runningAgents[a.id];

          return (
            <article key={a.id} className="border border-[var(--rule-light)] bg-white p-6" data-testid={`discovery-card-${a.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-display text-[22px] leading-none">{a.name}</div>
                <span className="text-[11px] font-mono text-[var(--muted-light)]">{a.discovered7d} found last 7d</span>
              </div>

              {a.lastRun && (
                <div className="mt-4 text-[12px] text-[var(--muted-light)]">
                  Last run: {fmtCET(a.lastRun.at)}
                  {" · "}<span className="text-[var(--ink)] font-mono">{a.lastRun.prospectsFound}</span> new found
                  {a.lastRun.ok ? <CheckCircle2 className="w-3 h-3 inline ml-1 text-emerald-500" /> : <span className="text-rose-500 ml-1">✗ failed</span>}
                </div>
              )}

              {/* Progress indicator while running */}
              {isRunning && (
                <div className="mt-4 flex items-center gap-2 text-[13px] text-[var(--blue)]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Discovery running — searching LinkedIn across {activeSet.size || 12} offers…</span>
                </div>
              )}

              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-2">Targeting offers</div>
                <div className="flex flex-wrap gap-1.5">
                  {offers.map((o) => {
                    const on = activeSet.has(o.id);
                    return (
                      <button key={o.id} onClick={() => toggleOffer(a.id, o.id, currentOfferIds)}
                        data-testid={`discovery-offer-${a.id}-${o.id}`}
                        className={`px-2 py-1 text-[11px] font-mono border transition-colors ${on ? "bg-[var(--blue)] border-[var(--blue)] text-white" : "border-[var(--rule-light)] text-[var(--muted-light)] hover:border-[var(--ink)]"}`}>
                        {o.id}
                      </button>
                    );
                  })}
                </div>
                {pendingOffers[a.id] !== undefined && (
                  <EditorialButton className="mt-3" variant="secondary"
                    onClick={() => saveOffers.mutate({ id: a.id, offers: [...activeSet] })}>
                    Save targeting
                  </EditorialButton>
                )}
              </div>

              {a.recentContacts?.length > 0 && (
                <div className="mt-6 border-t border-[var(--rule-light)] pt-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-2">Recently discovered</div>
                  <table className="w-full text-[12.5px]">
                    <tbody>
                      {a.recentContacts.map((r, i) => (
                        <tr key={i} className="border-b border-[var(--rule-light)] last:border-0">
                          <td className="py-2 pr-2 font-display text-[14px]">{r.name}</td>
                          <td className="py-2 pr-2 text-[var(--muted-light)] truncate max-w-[200px]">{r.headline}</td>
                          <td className="py-2 font-mono text-[var(--blue)]">#{r.offerId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <button
                onClick={() => runDiscover.mutate(a.id)}
                disabled={isRunning}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[var(--ink)] hover:bg-[var(--blue)] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-3 text-[11px] uppercase tracking-[0.24em] transition-colors"
                data-testid={`discovery-run-${a.id}`}>
                {isRunning
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running…</>
                  : <><Compass className="w-3.5 h-3.5" /> Run discovery now</>
                }
              </button>
            </article>
          );
        })}
      </section>

      {/* History */}
      <section className="px-8 lg:px-14 pb-16">
        <SectionTitle eyebrow="History">Discovery runs</SectionTitle>
        <div className="border border-[var(--rule-light)] overflow-y-auto max-h-[320px]">
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-left border-b border-[var(--ink)]">
                <Th>Date</Th><Th>Agent</Th>
                <Th align="right">Queries</Th><Th align="right">Found</Th>
              </tr>
            </thead>
            <tbody>
              {(data?.history ?? []).map((d) => (
                <tr key={d.id} className="border-b border-[var(--rule-light)]">
                  <td className="py-3 pr-4 pl-4 font-mono text-[12px]">{fmtCET(d.startedAt)}</td>
                  <td className="py-3 pr-4">{d.agentName}</td>
                  <td className="py-3 pr-4 text-right font-mono">{d.queriesRun}</td>
                  <td className="py-3 pr-4 text-right font-mono">{d.prospectsFound}</td>
                </tr>
              ))}
              {(data?.history ?? []).length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-[var(--muted-light)]">No discovery runs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`text-${align} text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] py-3 px-4`}>{children}</th>;
}
